import type { CommandStatus } from '../../hooks/useCommands.ts';
import { CMD_TONE, STAGE_LABELS, trackOf, trackTitle, type Slot } from './commandModel.ts';
import styles from './LifecycleTrack.module.css';

/* ==========================================================================
   The lifecycle track — a command's position in the queue's pipeline, drawn
   rather than spelled.

   Four slots: issue · sent · ack · settle. The slot the command is in now is
   the only one that carries the state's colour; the ones behind it are steel;
   the ones ahead are hollow. A `failed` or `cancelled` command draws its two
   middle slots HATCHED, because the contract records one terminal transition
   and no intermediate — we know it stopped, we do not know where, and a track
   that filled those in would be inventing a dispatch we never observed.

   Shape carries the state as well as hue (filled / hollow / hatched / struck),
   so the track survives a monochrome print and a colour-blind reader, and the
   whole thing reads out as one sentence to assistive tech.
   ========================================================================== */

/* 4 slots on a 16px pitch inside a 58×10 box: centres at 5, 21, 37, 53. */
const PITCH = 16;
const X0 = 5;
const W = 58;
const H = 10;
const R = 3;        /* half-width of a resting pip                          */
const RC = 4;       /* half-width of the current pip — the one marker       */

function cx(i: number): number {
  return X0 + i * PITCH;
}

/** Slots that mean "the command demonstrably got this far". */
const REACHED: ReadonlySet<Slot> = new Set<Slot>(['done', 'current', 'fail', 'cancel']);

const SLOT_CLASS: Record<Slot, string> = {
  done: styles.slotDone,
  current: styles.slotCurrent,
  open: styles.slotOpen,
  unknown: styles.slotUnknown,
  fail: styles.slotFail,
  cancel: styles.slotCancel,
};

export function LifecycleTrack({
  status,
  cell,
}: {
  status: CommandStatus;
  /** True where the track IS a cell of the queue's row grid. */
  cell?: true;
}) {
  const slots = trackOf(status);
  const tone = CMD_TONE[status];
  const label = `${status} — ${STAGE_LABELS.filter((_, i) => REACHED.has(slots[i] ?? 'open')).join(' → ')}`;

  return (
    <span
      className={styles.track}
      data-tone={tone}
      title={trackTitle(status)}
      role={cell === true ? 'gridcell' : 'img'}
      aria-label={label}
    >
      <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} aria-hidden="true" focusable="false">
        {slots.map((s, i) => {
          if (i === slots.length - 1) return null;
          const next = slots[i + 1] ?? 'open';
          const solid = REACHED.has(s) && REACHED.has(next);
          return (
            <line
              key={`c${i}`}
              className={solid ? styles.linkOn : styles.linkOff}
              x1={cx(i) + R + 1.5}
              y1={H / 2}
              x2={cx(i + 1) - R - 1.5}
              y2={H / 2}
            />
          );
        })}
        {slots.map((s, i) => {
          const half = s === 'current' || s === 'fail' ? RC : R;
          const x = cx(i) - half;
          const y = H / 2 - half;
          const side = half * 2;
          return (
            <g key={`p${i}`}>
              <rect
                className={SLOT_CLASS[s]}
                x={x}
                y={y}
                width={side}
                height={side}
              />
              {s === 'cancel' && (
                <line
                  className={styles.strike}
                  x1={cx(i) - half - 1}
                  y1={H / 2}
                  x2={cx(i) + half + 1}
                  y2={H / 2}
                />
              )}
            </g>
          );
        })}
      </svg>
    </span>
  );
}
